import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { AccountLibraryDeps } from '../library/AccountLibraryDeps'
import { accountCrmRequest } from './accountCrmRequest'

/**
 * A CRM call answered the way the CRM's own apiPost/apiGet answer it: the HTTP
 * status and the parsed JSON body, never thrown on a non-2xx status, so a page
 * can read the gateway envelope (`ok`, `status_code`, `data`, `detail`) itself.
 * Gateway calls go through the CRM pass-through (/crm/proxy/v1/…); `local`
 * calls reach routes the CRM serves itself (/crm/v1/…, its isLocalServerPath).
 */
export interface CrmResponse<T = unknown> {
  status: number
  data: T
}

export interface CrmRequestInit {
  method?: 'GET' | 'POST'
  body?: Record<string, unknown>
  local?: boolean
}

export type CrmRequest = <T = Record<string, unknown>>(path: string, init?: CrmRequestInit) => Promise<CrmResponse<T>>

const Ctx = createContext<CrmRequest | null>(null)

export function CrmRequestProvider({ value, children }: { value: CrmRequest; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useCrmRequest(): CrmRequest {
  const request = useContext(Ctx)
  if (!request) throw new Error('useCrmRequest outside CrmRequestProvider')
  return request
}

/** Everything a React CRM page needs for one account: the Library deps plus raw CRM requests. */
export function AccountCrmPageDeps({ accountId, children }: { accountId: string; children: ReactNode }) {
  const request = useMemo(() => accountCrmRequest(accountId), [accountId])
  return (
    <AccountLibraryDeps accountId={accountId}>
      <CrmRequestProvider value={request}>{children}</CrmRequestProvider>
    </AccountLibraryDeps>
  )
}
